import { useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { useState } from 'react';

// Global brand context: applies firm's primary/secondary colors + logo to CSS variables
export default function BrandProvider({ children }) {
  const { user } = useAuth();
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    // fetch settings for authenticated user; public for anonymous
    const url = user ? '/settings' : '/settings/public';
    api.get(url).then((r) => setFirm(r.data)).catch(() => {});
  }, [user?.id]);

  const styleTag = useMemo(() => {
    const primary = firm?.brand_primary_color || '#132a4d';
    const secondary = firm?.brand_secondary_color || '#c9a961';
    // Note: consumers use `--brand-primary` for firm color while keeping shadcn theme intact.
    return `:root { --brand-primary: ${primary}; --brand-secondary: ${secondary}; }
            .brand-bg { background-color: var(--brand-primary); }
            .brand-text { color: var(--brand-primary); }
            .brand-ring:focus-visible { box-shadow: 0 0 0 3px color-mix(in oklab, var(--brand-primary) 25%, transparent); }
            .brand-border { border-color: var(--brand-primary); }`;
  }, [firm]);

  return (
    <>
      <style>{styleTag}</style>
      <FirmContext.Provider value={{ firm, setFirm }}>{children}</FirmContext.Provider>
    </>
  );
}

import { createContext, useContext } from 'react';
export const FirmContext = createContext({ firm: null, setFirm: () => {} });
export const useFirm = () => useContext(FirmContext);

// Small reusable Logo mark: shows firm logo image OR falls back to the
// EZ DockIT platform monogram. Firms can upload their own logo in
// Settings → Branding to override this.
export function FirmMark({ size = 40, className = '', variant = 'default' }) {
  const { firm } = useFirm();
  const name = firm?.firm_name || 'EZ DockIT';

  if (firm?.logo_url) {
    return (
      <img
        src={firm.logo_url}
        alt={name}
        style={{ height: size, width: size }}
        className={`rounded-lg object-contain bg-white border border-slate-200 ${className}`}
      />
    );
  }
  // Default brand mark — the firm's ERG monogram
  const wrapperBg = variant === 'onDark'
    ? 'bg-white/95 border-white/20 shadow-sm'
    : 'bg-white border-slate-200';
  return (
    <div
      style={{ height: size, width: size }}
      className={`rounded-lg overflow-hidden flex items-center justify-center border ${wrapperBg} ${className}`}
    >
      <img
        src="/brand/erg-logo.webp"
        alt={name}
        style={{ height: size * 0.86, width: size * 0.86 }}
        className="object-contain"
      />
    </div>
  );
}
